import { useLang } from "../i18n";
import { useSEO } from "../seo";
import { FadeIn } from "../components/Reveal";
import { CTAButton, PageHero, SectionLabel } from "../components/Bits";

const IMG = {
  hero: "https://images.unsplash.com/photo-1567188466004-4d4b003f5c49?q=80&w=2000&auto=format&fit=crop",
};

const copy = {
  it: {
    seoTitle: "Consulenza Immobiliare Strategica | Nicole Srl",
    seoDesc: "Consulenza immobiliare strategica: analisi del potenziale, frazionamenti, cambi di destinazione d'uso, riposizionamento e strategia di valorizzazione.",
    kicker: "Consulenza immobiliare",
    lines: ["Prima di investire.", "Prima di ristrutturare.", "Prima di vendere.", "Analizziamo l'operazione."],
    sub: "Consulenza immobiliare strategica per individuare il potenziale dell'immobile e costruire una strategia di valorizzazione.",
    label: "Cosa analizziamo",
    blocks: [
      { n: "01", title: "Analisi dell'immobile", text: "Analisi delle caratteristiche dell'immobile, della posizione, della configurazione e delle principali criticità." },
      { n: "02", title: "Analisi del potenziale", text: "Individuazione delle possibili strategie di trasformazione e valorizzazione." },
      { n: "03", title: "Frazionamento", text: "Studio preliminare delle possibilità di suddivisione dell'immobile in più unità." },
      { n: "04", title: "Cambio di destinazione d'uso", text: "Analisi preliminare delle possibilità di trasformazione funzionale dell'immobile." },
      { n: "05", title: "Riposizionamento", text: "Definizione del prodotto immobiliare finale, del target e del livello di intervento più coerente." },
      { n: "06", title: "Strategia", text: "Confronto tra costi, complessità, potenziale valore finale e possibili alternative." },
    ],
    noteTitle: "Il nostro ruolo",
    noteText:
      "Nicole Srl non svolge attività di intermediazione o mediazione immobiliare. La consulenza riguarda esclusivamente l'analisi, la strategia, la valorizzazione e lo sviluppo dell'operazione immobiliare. Quando sono necessarie attività tecniche, urbanistiche, catastali, notarili o legali riservate a professionisti abilitati, operiamo in coordinamento con i professionisti incaricati.",
    ctaTitle: "Parlaci del tuo immobile",
    ctaText: "Descrivici l'immobile e l'obiettivo: ti rispondiamo con una prima lettura riservata dell'operazione.",
  },
  en: {
    seoTitle: "Strategic Real Estate Advisory | Nicole Srl",
    seoDesc: "Strategic real estate advisory: potential analysis, subdivision, change of use, repositioning and value-creation strategy.",
    kicker: "Real estate advisory",
    lines: ["Before investing.", "Before renovating.", "Before selling.", "We analyse the operation."],
    sub: "Strategic real estate advisory to identify a property's potential and build a value-creation strategy.",
    label: "What we analyse",
    blocks: [
      { n: "01", title: "Property analysis", text: "Analysis of the property's features, location, configuration and main criticalities." },
      { n: "02", title: "Potential analysis", text: "Identification of possible transformation and value-creation strategies." },
      { n: "03", title: "Subdivision", text: "Preliminary study of the possibilities of dividing the property into multiple units." },
      { n: "04", title: "Change of use", text: "Preliminary analysis of the possibilities of functional transformation of the property." },
      { n: "05", title: "Repositioning", text: "Definition of the final real estate product, its target and the most coherent level of intervention." },
      { n: "06", title: "Strategy", text: "Comparison of costs, complexity, potential final value and possible alternatives." },
    ],
    noteTitle: "Our role",
    noteText:
      "Nicole Srl does not carry out real estate brokerage or agency activities. The advisory covers exclusively the analysis, strategy, enhancement and development of the real estate operation. Where technical, planning, cadastral, notarial or legal activities reserved to licensed professionals are required, we operate in coordination with the appointed professionals.",
    ctaTitle: "Tell us about your property",
    ctaText: "Describe the property and the objective: we will reply with a first confidential reading of the operation.",
  },
};

export default function Consulenza() {
  const { lang } = useLang();
  const t = copy[lang];
  useSEO({ title: t.seoTitle, description: t.seoDesc, path: "/consulenza", lang });

  return (
    <div data-testid="consulenza-page">
      <PageHero kicker={t.kicker} lines={t.lines} sub={t.sub} image={IMG.hero} imageAlt={lang === "it" ? "Dettaglio architettonico — lucernari" : "Architectural detail — skylights"} />

      <section className="mx-auto max-w-7xl px-6 py-24 md:px-10 md:py-36">
        <FadeIn>
          <SectionLabel>{t.label}</SectionLabel>
        </FadeIn>
        <div className="mt-16 grid gap-px bg-ink/10 md:mt-24 md:grid-cols-2 lg:grid-cols-3">
          {t.blocks.map((b, i) => (
            <FadeIn key={b.n} delay={(i % 3) * 0.07} className="bg-ivory">
              <div className="group flex h-full flex-col p-8 transition-colors duration-500 hover:bg-dove/50 md:p-10">
                <span className="font-serif text-4xl font-light text-ink/15 transition-colors duration-500 group-hover:text-bronze">
                  {b.n}
                </span>
                <h2 className="mt-6 font-serif text-2xl font-light tracking-tight md:text-[1.7rem]">{b.title}</h2>
                <p className="mt-4 text-sm leading-relaxed text-stone">{b.text}</p>
              </div>
            </FadeIn>
          ))}
        </div>

        <FadeIn delay={0.1}>
          <div className="mt-20 border-l-2 border-bronze bg-dove/40 p-8 md:p-12">
            <h2 className="text-[11px] font-medium uppercase tracking-[0.25em] text-stone">{t.noteTitle}</h2>
            <p className="mt-5 max-w-3xl text-base leading-relaxed">{t.noteText}</p>
          </div>
        </FadeIn>
      </section>

      <section className="border-t border-ink/10">
        <div className="mx-auto max-w-7xl px-6 py-24 text-center md:px-10 md:py-36">
          <FadeIn>
            <h2 className="mx-auto max-w-3xl font-serif text-4xl font-light leading-[1.08] tracking-tight md:text-5xl">
              {t.ctaTitle}
            </h2>
            <p className="mx-auto mt-6 max-w-md text-base leading-relaxed text-stone">{t.ctaText}</p>
            <div className="mt-10 flex justify-center">
              <CTAButton to="/contatti" variant="dark" testId="consulenza-page-cta">
                {lang === "it" ? "Parlaci del tuo immobile" : "Tell us about your property"}
              </CTAButton>
            </div>
          </FadeIn>
        </div>
      </section>
    </div>
  );
}
